import React, {useState, useRef} from 'react';
import axios from 'axios';
import {
    Input,
    CustomFileButton,
    ModalBody,
    ModalFooter,
    ModalHeader,
    StyledDialog,
    DropZone
} from './AddDocumentDialog.styled';
import CircularProgress from '@mui/material/CircularProgress';
import {useSnackbar} from '../../contexts/SnackbarContext';
import {UploadDocumentResponse} from "../../models/models";
import {ActionButton, CancelButton} from '../common/ActionButtons';
import {useFileValidation} from '../../utils/fileValidation';
import {useDragAndDrop} from '../../hooks/useDragAndDrop';
import {useTranslation} from 'react-i18next';

interface Props {
    open: boolean;
    onClose: () => void;
    dataSourceId: string,
    onDocumentCreated: () => void;
}

const AddDocumentDialog: React.FC<Props> = ({open, onClose, dataSourceId, onDocumentCreated}) => {
    const {t} = useTranslation();
    const [newDocumentFiles, setNewDocumentFiles] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const {showSnackbar} = useSnackbar();
    const { validateFile } = useFileValidation();
    const formRef = useRef<HTMLFormElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFilesAccepted = (files: File[]) => {
        setNewDocumentFiles(prevFiles => [...prevFiles, ...files]);
    };

    const {
        isDraggingOver,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop
    } = useDragAndDrop({
        onFileAccepted: handleFilesAccepted,
        validateFile,
        disabled: isLoading,
        currentFiles: newDocumentFiles
    });

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (newDocumentFiles.length === 0 || !dataSourceId) {
            showSnackbar(t('addDocument.pleaseSelectFile'), 'error');
            return;
        }

        if (isLoading) return;

        setIsLoading(true);
        try {
            await uploadDocument(newDocumentFiles, dataSourceId);

            showSnackbar(newDocumentFiles.length > 1 ? t('addDocument.documentsAddedSuccess') : t('addDocument.documentAddedSuccess'), 'success');
            onDocumentCreated();
            closeModal();
        } catch (error) {
            console.error('Failed to upload document:', error);
            showSnackbar(t('addDocument.uploadError'), 'error');
            setIsLoading(false);
        }
    };

    const uploadDocument = async (files: File[], collectionId?: string): Promise<UploadDocumentResponse> => {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append(`files`, file);
        });
        if (collectionId) {
            formData.append('collectionId', collectionId);
        }

        const response = await axios.post<UploadDocumentResponse>('/documents', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data;
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const files = Array.from(event.target.files);
        const validFiles: File[] = [];

        for (const file of files) {
            const validationResult = validateFile(file);
            if (validationResult.isValid) {
                validFiles.push(file);
            }
        }

        if (validFiles.length > 0) {
            setNewDocumentFiles(prevFiles => [...prevFiles, ...validFiles]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setNewDocumentFiles(prevFiles => {
            const newFiles = [...prevFiles];
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    const closeModal = () => {
        setNewDocumentFiles([]);
        setIsLoading(false);
        onClose();
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <StyledDialog
            open={open}
            onClose={isLoading ? undefined : closeModal}
            disableEscapeKeyDown={isLoading}
        >
            <ModalHeader>{t('addDocument.title')}</ModalHeader>
            <form
                ref={formRef}
                onSubmit={handleSubmit}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <ModalBody>
                    <DropZone $isDraggingOver={isDraggingOver}>
                        <p>{t('addDocument.dragAndDrop')}</p>
                        <Input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.txt,.docx,.xlsx,.pptx"
                            onChange={handleFileChange}
                            multiple
                            required={newDocumentFiles.length === 0}
                        />
                        <CustomFileButton
                            type="button"
                            onClick={handleButtonClick}
                            disabled={isLoading}
                        >
                            {t('addDocument.selectFiles')}
                        </CustomFileButton>
                        <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                            {t('addDocument.acceptableFormats')}<br />
                            {t('addDocument.maxFileSize')}
                        </p>
                        {newDocumentFiles.length > 0 && (
                            <div>
                                <p>{t('addDocument.selectedFiles')}</p>
                                <ul style={{ listStyleType: 'none', padding: 0 }}>
                                    {newDocumentFiles.map((file, index) => (
                                        <li key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <span>{file.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile(index)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: '#042f98',
                                                    fontSize: '16px'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </DropZone>
                </ModalBody>
                <ModalFooter>
                    <CancelButton
                        onClick={closeModal}
                        disabled={isLoading}
                    >
                        {t('addDocument.cancel')}
                    </CancelButton>
                    <ActionButton
                        type="submit"
                        disabled={isLoading || newDocumentFiles.length === 0}
                    >
                        {isLoading ? <CircularProgress size={24} color="inherit"/> : t('addDocument.add')}
                    </ActionButton>
                </ModalFooter>
            </form>
        </StyledDialog>
    );
};

export default AddDocumentDialog;
